// AI Money Factory end-of-module quizzes (Dew, 2026-10-19): active recall of each module's key ideas,
// written from the lesson videos' transcripts + lesson text (work/lms/ai-sales-system/quiz/sources).
// One set per module, id "amf-quiz-m<N>"; stored like any check-in (checkpoints/{uid}_{courseId}_{setId}).
import type { CheckpointQuestion, CheckpointSet } from "./checkpoints";

/** One question: options in display order, `right` = 1-based index of the correct option. */
const q = (id: string, text: string, options: string[], right: number, explain: string): CheckpointQuestion => ({
  id,
  text,
  options: options.map((label, i) => ({ id: String(i + 1), label })),
  answer: String(right),
  explain,
});

const quiz = (n: number, title: string, questions: CheckpointQuestion[]): CheckpointSet => ({
  id: `amf-quiz-m${n}`,
  title: `Quiz ท้าย Module ${n}: ${title}`,
  intro: `${questions.length} ข้อ ทบทวนสิ่งสำคัญของ Module นี้ ตอบจากความจำก่อน แล้วค่อยดูเฉลย ทำซ้ำได้เรื่อยๆ`,
  questions,
  quiz: true,
});

const M1 = quiz(1, "หาของที่จะขาย", [
  q("q1", "Digital Product คืออะไร", [
    "ของที่ขายผ่านหน้าร้านออนไลน์ แล้วส่งพัสดุให้ลูกค้า",
    "ของที่ขายเป็นไฟล์หรือเป็นระบบ ส่งให้ลูกค้าผ่านออนไลน์ได้เลย ไม่ต้องแพ็กของ",
    "บริการที่ต้องนัดเจอลูกค้าตัวต่อตัวทุกครั้ง",
    "สินค้าที่ต้องสต็อกไว้ก่อนขาย",
  ], 2, "คอร์ส ebook template ซอฟต์แวร์ หรือ Agent Skill คือ Digital Product ทั้งหมด เพราะส่งให้ลูกค้าทางออนไลน์ได้ทันที"),
  q("q2", "ซอฟต์แวร์หรือ Agent Skill ขายได้เพราะอะไร", [
    "เพราะเขียนโค้ดเยอะ ดูซับซ้อน",
    "เพราะใช้ AI รุ่นใหม่ล่าสุด",
    "เพราะข้างในมี Business Logic ที่แก้ปัญหาให้ลูกค้า เอาไปใช้แล้วปัญหาหายเลย",
    "เพราะราคาถูกกว่าคอร์ส",
  ], 3, "เช่น Notion แก้ปัญหาจดโน้ตไม่เป็นระเบียบ Canva แก้ปัญหาทำสไลด์ช้า สิ่งที่ลูกค้าจ่ายคือการแก้ปัญหา ไม่ใช่ตัวโค้ด"),
  q("q3", "ข้อไหน ไม่ใช่ 1 ใน 3 แหล่งหาไอเดีย Digital Product ที่ดิวแนะนำ", [
    "ตัวคุณเอง: ทำอะไรได้ดีกว่าคนรอบตัว คนชอบถามหรือชมเรื่องอะไร",
    "สินค้าที่กำลังลดราคาเยอะๆ ในแพลตฟอร์มขายของ",
    "คนที่สำเร็จแล้วและมีสกิลคล้ายคุณ (IG link in bio, Skool, Udemy, MEB, Gumroad)",
    "Creator ที่โผล่ในฟีดคุณบ่อยๆ ว่าเขาขายอะไร",
  ], 2, "3 แหล่งคือ ตัวเอง, คนที่สำเร็จแล้วที่สกิลคล้ายคุณ และ Creator ในฟีด ของลดราคาไม่ได้บอกว่าคนยอมจ่ายเพื่อแก้ปัญหาอะไร"),
  q("q4", "ประโยครวมไอเดียคือ \"ผมจะช่วย [ใคร] ให้ [ได้ผลลัพธ์อะไร] ผ่าน [...]\" ช่องสุดท้ายคืออะไร", [
    "ราคาสินค้า",
    "รูปแบบของ เช่น คอร์ส หนังสือ Template หรือ Skill",
    "ชื่อแพลตฟอร์มที่จะโพสต์",
    "จำนวนลูกค้าที่อยากได้",
  ], 2, "ถ้าเติมครบ 3 ช่อง (ใคร, ผลลัพธ์, รูปแบบ) แปลว่าคุณมีไอเดียแล้ว เช่น ช่วยคนที่ใช้ AI gen ภาพ ให้ได้ภาพที่ไม่เป็น AI slop ผ่านคอร์สสอนวิธีคิด prompt"),
  q("q5", "ได้ไอเดียแล้ว ควรทำอะไรเป็นอย่างแรก", [
    "ทำของให้เสร็จก่อน แล้วค่อยหาคนซื้อ",
    "ทำเว็บไซต์หน้าขายให้สวยก่อน",
    "ยิงแอดทดสอบเลย",
    "ทำคอนเทนต์ทดสอบตลาดก่อน ดูว่ามีคนสนใจไหม แล้วค่อยลงมือทำของ",
  ], 4, "ดิวทดสอบด้วยการโพสต์ภาพ AI แจก prompt ก่อน พอมีคนคอมเมนต์ร้อยกว่าคนถึงค่อยเปิด pre-order แล้วทำคอร์ส"),
  q("q6", "แค่ไหนถึงเรียกว่า \"มีคนสนใจพอ\" ให้ทำต่อได้", [
    "มีคนคอมเมนต์ว่า \"สนใจ\" ประมาณ 10-20 คน แล้วเปิด Pre-order",
    "มีคนกดไลก์ 1,000 ไลก์ขึ้นไป",
    "มีคนแชร์อย่างน้อย 100 แชร์",
    "ต้องมีคนโอนเงินเต็มจำนวนก่อนเท่านั้น",
  ], 1, "10-20 คอมเมนต์ว่าสนใจก็ไปต่อได้ จากนั้นเปิด Pre-order ได้คนจองแล้วค่อยทำของ"),
  q("q7", "ถ้าทดสอบตลาดด้วยโพสต์ Facebook หรือบทความ ต้องทำอะไรเพิ่ม", [
    "ไม่ต้องทำอะไร รอให้ระบบดันเอง",
    "ซื้อไลก์เพิ่ม",
    "เอาไปแชร์ลงกลุ่มที่ตรงกับเรื่องนั้นด้วย ไม่งั้นแทบไม่มีใครเห็น",
    "โพสต์ซ้ำทุกชั่วโมง",
  ], 3, "โพสต์ FB ต้องแชร์ลงกลุ่ม เช่น เรื่องภาพ AI แชร์ลงกลุ่มสาย Creative ส่วนคลิปสั้น IG/TikTok แพลตฟอร์มดันให้อยู่แล้ว"),
  q("q8", "คลิปสั้นทดสอบตลาดต้องมี 4 อย่าง ข้อไหน ไม่ใช่ 1 ใน 4", [
    "พูดหน้ากล้อง",
    "Hook ที่ดึงให้ดูต่อ",
    "ราคาสินค้าและลิงก์จ่ายเงิน",
    "CTA ชวนคนที่สนใจให้คอมเมนต์ว่า \"สนใจ\"",
  ], 3, "4 อย่างคือ พูดหน้ากล้อง, Hook, Use Case ของคุณ และ CTA ให้คอมเมนต์ ตอนทดสอบยังไม่ต้องมีของหรือราคา"),
  q("q9", "ทำไมของ 1 ชิ้น ควรแก้แค่ 1 ปัญหา ให้ลูกค้า 1 กลุ่ม", [
    "เพราะกฎหมายห้ามขายของที่แก้หลายปัญหา",
    "เพราะโฟกัสน้อย ทำคอนเทนต์ดึงลูกค้าได้ตรงเป้า ถ้าแก้หลายปัญหาจะต้องทำคอนเทนต์หลายมุมจนโฟกัสกระจาย",
    "เพราะลูกค้าไม่ชอบของที่ทำได้หลายอย่าง",
    "เพราะทำให้ตั้งราคาสูงได้ทันที",
  ], 2, "1 ปัญหา 1 Offer 1 กลุ่มลูกค้า ดิวยกคอร์สนี้เองเป็นตัวอย่างที่ไม่ควรทำตาม เพราะแก้หลายปัญหาเกินไป"),
  q("q10", "Reframe คืออะไร", [
    "การลดราคาให้ถูกกว่าคู่แข่ง",
    "การเปลี่ยนแพลตฟอร์มที่ใช้ขาย",
    "การเปลี่ยนชื่อ Offer จากแบบตรงไปตรงมา ให้เป็นมุมที่ไม่ซ้ำตลาดและน่าสนใจ",
    "การทำสินค้าใหม่ทั้งหมดเมื่อขายไม่ออก",
  ], 3, "ชื่อแบบตรงไปตรงมาจะกลายเป็น noise ที่ใครๆ ก็ขาย การ reframe ทำให้ของเดิมดูไม่ซ้ำใคร"),
  q("q11", "ตัวอย่าง Reframe ข้อไหนถูกต้อง", [
    "\"ระบบอัตโนมัติขายดิจิทัลโปรดักต์\" เป็น \"โรงงานผลิตวิดีโอ\"",
    "\"โรงงานผลิตวิดีโอ\" เป็น \"ระบบอัตโนมัติขายดิจิทัลโปรดักต์\"",
    "\"คอร์สจดโน้ต\" เป็น \"คอร์สจดโน้ตฉบับสมบูรณ์\"",
    "\"คอร์สทำธุรกิจ\" เป็น \"คอร์สทำธุรกิจราคาพิเศษ\"",
  ], 1, "Reframe คือเปลี่ยนจากชื่อที่บอกตรงๆ ไปเป็นภาพที่จำง่ายไม่ซ้ำใคร แบบ Building a Second Brain (สอนจดโน้ต) หรือ 90 Day One Person Business (สอนทำธุรกิจ)"),
  q("q12", "ดิวลืม Reframe โปรดักต์ตัวที่ 3 และ 4 ผลเป็นยังไง", [
    "ขายดีกว่าตัวแรก",
    "ขายแทบไม่ออกเลย",
    "ขายได้เท่าเดิม",
    "ไม่มีผลอะไร เพราะชื่อไม่สำคัญ",
  ], 2, "โปรดักต์แรกที่ reframe แล้วสำเร็จมาก ส่วนตัวที่ลืม reframe ขายแทบไม่ออก ดิวเลยย้ำว่าขั้นนี้ห้ามข้าม"),
  q("q13", "โพสต์ทดสอบตลาดโพสต์แรกเงียบ ไม่มีคนสนใจ ควรทำยังไง", [
    "เปลี่ยนไอเดียโปรดักต์ทันที",
    "เลิกทำ เพราะแปลว่าตลาดไม่ต้องการ",
    "อย่าเพิ่งเปลี่ยนไอเดีย ให้เปลี่ยนมุมเล่าแล้วโพสต์ใหม่ไปเรื่อยๆ",
    "ยิงแอดโพสต์เดิมให้คนเห็นเยอะขึ้น",
  ], 3, "ดิวเองโพสต์หลายรอบกว่าจะเจอโพสต์ \"ผมสร้างโรงงานผลิตวิดีโอ\" ที่คนสนใจเยอะ แล้วค่อยเปิดขาย"),
  q("q14", "วิธีหาไอเดียที่ดิวบอกว่า \"ไวกว่า\" คืออะไร", [
    "หาโปรดักต์ที่ต่างประเทศพูดถึงแล้ว แต่ในไทยยังไม่ค่อยมีใครทำ",
    "ถามเพื่อนสนิทว่าอยากซื้ออะไร",
    "รอให้มีคนมาจ้างก่อน",
    "ทำของที่ตัวเองชอบโดยไม่ต้องดูตลาด",
  ], 1, "ดิวดูจาก Skool และ IG ว่าต่างประเทศขายอะไรกัน เช่น สอนภาษาญี่ปุ่นสำหรับคนไปเที่ยว หรือสอนเล่นเกม CS2"),
  q("q15", "ถ้าคิดว่าตัวเองไม่มีสกิลอะไรจะขาย ดิวแนะนำให้ทำอะไร", [
    "รอจนเก่งที่สุดในวงการก่อนค่อยเริ่ม",
    "ไปเรียนเพิ่ม เช่น สกิลสาย AI แล้วรับงาน service ก่อน พอเห็น insight ค่อยทำเป็น Digital Product",
    "ก๊อปคอร์สคนอื่นมาขาย",
    "ข้ามไปยิงแอดเลย",
  ], 2, "ไม่ต้องเก่งเว่อวัง ขอแค่มีสกิลที่ช่วยคนได้ ตัวอย่าง AI-sthetics ที่เพื่อนดิวไปเรียนหลายที่แล้วพัฒนาเป็นเฟรมเวิร์กของตัวเอง"),
]);

const M2 = quiz(2, "ทำคอนเทนต์ให้คนเห็นและอยากซื้อ", [
  q("q1", "Content Funnel 3 ระดับ ทำหน้าที่อะไรตามลำดับ Top / Middle / Bottom", [
    "ปิดการขาย / ทำให้คนรู้จัก / ทำให้คนเชื่อใจ",
    "ทำให้คนรู้จักเรา / ทำให้คนเชื่อว่าเราช่วยเขาได้ / ปิดการขาย",
    "ทำให้คนเชื่อใจ / ปิดการขาย / ทำให้คนรู้จัก",
    "ทั้ง 3 ระดับทำหน้าที่เดียวกัน คือให้ได้ยอดวิว",
  ], 2, "ถ้าไม่เข้าใจ funnel จะทำคอนเทนต์ที่คนดูเยอะแต่ขายไม่ได้ เพราะมีแต่ Top ไม่มี Middle กับ Bottom"),
  q("q2", "คอนเทนต์แบบไหนเป็น Top Funnel", [
    "Sales page ของคอร์ส",
    "รีวิวจากลูกค้า (testimonial)",
    "เรื่องที่สื่อกำลังสนใจ (media attention) หรือความรู้แนว \"Did you know\"",
    "โพสต์แจ้งว่าจะขึ้นราคาพรุ่งนี้",
  ], 3, "Top Funnel คือทำยังไงก็ได้ให้คนเห็น เช่น ตอนเครื่องมือ AI ตัวใหม่เป็นกระแส ก็เล่าเรื่องนั้นพร้อม use case ที่เราใช้จริง"),
  q("q3", "Knowledge Content แนว \"How to\" อยู่ใน Funnel ไหน และทำไม", [
    "Middle Funnel เพราะทำให้คนเห็นว่าเรามี expertise จริง",
    "Top Funnel เพราะคนแชร์เยอะที่สุด",
    "Bottom Funnel เพราะปิดการขายได้ทันที",
    "ไม่อยู่ใน Funnel ไหนเลย",
  ], 1, "Did you know ทำให้คนอยากรู้ (Top) ส่วน How to ทำให้คนเชื่อว่าเราทำเป็นจริง (Middle)"),
  q("q4", "คอนเทนต์ Middle Funnel มักได้คนสนใจน้อยกว่า Top แล้วควรทำยังไง", [
    "เลิกทำ ทำแต่คอนเทนต์ไวรัลก็พอ",
    "ยังต้องทำ เพราะถ้าจะขายของต้องมีคอนเทนต์ที่สร้างความเชื่อใจ ไม่ใช่เอาแต่ไวรัล",
    "ยิงแอดดันทุกชิ้น",
    "เปลี่ยนทุกชิ้นเป็น Bottom Funnel",
  ], 2, "Middle Funnel ไม่ใช่กระแส คนเลยสนใจน้อยลงแน่นอน แต่มันคือส่วนที่ทำให้คนรู้ว่าเราช่วยเขาได้จริง"),
  q("q5", "ทำไม Sales page ถึงเป็น Bottom Funnel ที่ \"โหดที่สุด\"", [
    "เพราะใช้เวลาทำนานที่สุด",
    "เพราะได้ยอดวิวเยอะที่สุด",
    "เพราะอธิบายทุกอย่างที่ทำให้คนตัดสินใจซื้อไว้ในที่เดียว",
    "เพราะไม่ต้องมีรูปหน้าเรา",
  ], 3, "Bottom Funnel อื่นๆ เช่น testimonial โปรโมชัน หรือประกาศขึ้นราคา ช่วยเร่งการตัดสินใจ แต่ Sales page รวมเหตุผลให้ซื้อครบที่สุด"),
  q("q6", "\"กฎของไวรัล\" ที่ดิวเปรียบเหมือนกฎฟิสิกส์ คืออะไร", [
    "ทุกคอนเทนต์มีโอกาสไวรัลเท่ากัน",
    "ต้องลงตอนเที่ยงคืนถึงจะไวรัล",
    "หัวข้อที่เคยไวรัลแล้ว เอามาเล่าอีกก็ไวรัลอีก (ยกเว้นบางเรื่องที่ไวรัลแค่ช่วงเวลาหนึ่ง เช่น ข่าวเทค)",
    "คอนเทนต์ยิ่งยาวยิ่งไวรัล",
  ], 3, "เพราะฉะนั้นวิธีหาหัวข้อที่ดีที่สุดคือดูว่าหัวข้อไหนเคยไวรัลมาแล้ว แล้วเอามาเล่าใหม่ในมุมของเรา"),
  q("q7", "เวลาไปส่องช่องคู่แข่งเพื่อหาหัวข้อไวรัล ต้องดูอะไร", [
    "เฉพาะคลิปที่ได้ล้านวิวขึ้นไป",
    "คลิปที่วิวโดดออกมาจากค่าเฉลี่ยของช่องนั้นเอง",
    "คลิปล่าสุดของช่อง",
    "คลิปที่คอมเมนต์น้อยที่สุด",
  ], 2, "ช่องที่ปกติได้พันวิวแล้วมีคลิปเดียวได้หมื่นวิว คือสัญญาณ เช่น ช่อง LegFrost ปกติหลักพัน แต่มีคลิปเดียว 70,000 วิว"),
  q("q8", "เจอหัวข้อไวรัลของคู่แข่งแล้ว ควรเอามาใช้ยังไง", [
    "ก๊อปมาทั้งคลิป แค่เปลี่ยนเสียงพูด",
    "แปลเป็นภาษาไทยแล้วโพสต์ทันที",
    "เอาหัวข้อมาเล่าในมุมของเรา บวกประสบการณ์ของเราเข้าไป แล้วแตกเป็นหลายมุมได้อีก",
    "ไม่ควรใช้ เพราะเคยมีคนทำไปแล้ว",
  ], 3, "ห้ามก๊อปตรงๆ ดิวหยิบหัวข้อจากช่อง LegFrost มาเล่าในมุมตัวเอง ได้ 3,000 แชร์ มากกว่าต้นฉบับอีก"),
  q("q9", "ในสูตรคอนเทนต์ \"คุณค่า ยกกำลังด้วย hook\" ถ้า hook เป็นศูนย์จะเกิดอะไร", [
    "คุณค่าทั้งหมดก็เป็นศูนย์ เพราะคนไม่หยุดดูตั้งแต่แรก",
    "ไม่เป็นไร ถ้าเนื้อหาดีพอ",
    "ได้ยอดวิวครึ่งหนึ่ง",
    "algorithm จะดันให้อัตโนมัติ",
  ], 1, "hook เป็นเลขยกกำลัง จึงสำคัญที่สุด เนื้อหาดีแค่ไหน ถ้าไม่มีใครหยุดดู ก็ไม่มีใครได้คุณค่านั้น"),
  q("q10", "ตาม first principle ของดิว hook มีหน้าที่อะไร", [
    "บอกราคาสินค้า",
    "สรุปเนื้อหาทั้งหมดให้จบในประโยคแรก",
    "ทักทายคนดูให้เป็นกันเอง",
    "ทำให้คนสงสัยว่าจะเกิดอะไรขึ้น",
  ], 4, "วิธีตรวจ hook ที่ AI เขียนมาคือถามคำถามเดียว: อ่านแล้วสงสัยไหม อยากรู้ต่อไหม ถ้าไม่ ก็ให้เขียนใหม่"),
  q("q11", "\"ใครขายของออนไลน์คนเดียว ไม่มีทีม อันนี้เขียนให้คุณโดยเฉพาะ\" เป็น hook แบบไหน", [
    "แบบช็อก",
    "แบบเรียกตัวตน",
    "แบบเปิดปมค้าง",
    "แบบจี้ใจ",
  ], 2, "hook 4 แบบ: ช็อก (เกินคาด), จี้ใจ (โดนความเจ็บ), เรียกตัวตน (พูดกับคนกลุ่มเดียวตรงๆ), เปิดปมค้าง (ต้องดูต่อถึงจะรู้)"),
  q("q12", "ข้อไหนคือสัญญาณว่า hook ที่ AI เขียนมา ไม่ผ่าน", [
    "บอกได้ชัดว่าเป็น hook แบบไหนใน 4 แบบ",
    "ใช้ตัวเลขจริงของเราเอง",
    "อ่านแล้วอยากรู้ต่อ",
    "เปิดด้วยการเกริ่นว่า \"วันนี้จะมาเล่าเรื่อง...\" หรือใส่ตัวเลขที่เราไม่ได้ให้ไป",
  ], 4, "AI แต่งตัวเลขได้เนียนมาก ถ้ามีคนถามหาที่มาเราจะเสียความน่าเชื่อถือ ส่วนการเกริ่นไม่มีแรงดึงอะไรเลย"),
  q("q13", "ถ้าเพิ่งเริ่มจากศูนย์ ไม่มีผู้ติดตาม แพลตฟอร์มไหนไวรัลได้ง่ายที่สุด และเพราะอะไร", [
    "Facebook เพราะคนอายุเยอะอ่านโพสต์ยาว",
    "YouTube เพราะเป็นคลิปยาว",
    "TikTok เพราะหน้า For You กระจายคอนเทนต์ตาม performance ของแต่ละคลิป ไม่ใช่ตามจำนวน follower",
    "Instagram Story เพราะคนดูเยอะที่สุด",
  ], 3, "บน TikTok คนมี 100 follower กับ 1 ล้าน follower แข่งกันบนเส้นเดียวกันในทุกคลิป"),
  q("q14", "Cheat Code ที่ใช้ได้กับทุกแพลตฟอร์มคืออะไร", [
    "ใช้ hashtag ให้เยอะที่สุด",
    "ทำให้คนอยู่กับคอนเทนต์นานๆ หรือดูจนจบ (watch-time / completion rate) เพราะแพลตฟอร์มอยากให้คนอยู่นาน",
    "โพสต์ตอนคนออนไลน์น้อย จะได้ไม่มีคู่แข่ง",
    "ขอให้คนกดไลก์ เพราะไลก์เป็น signal ที่แรงที่สุด",
  ], 2, "ไลก์เป็น signal ที่อ่อนที่สุด ตัวที่หนักกว่าคือ share, save, comment และการดูจนจบ ทำได้ด้วยการเปิดปมใหม่ทุกซีน"),
  q("q15", "เรื่องความถี่ในการลงคอนเทนต์ ข้อไหนตรงกับที่ดิวสอน", [
    "ลงนานๆ ครั้งแต่เนี้ยบที่สุด ดีกว่าลงบ่อย",
    "ลงอาทิตย์ละครั้งก็พอ",
    "ลงทุกวันแบบคุณภาพพอใช้ได้ ดีกว่าลงนานๆ ที ความสม่ำเสมอชนะความสมบูรณ์แบบ",
    "ลงวันละ 10 ชิ้นขึ้นไปเท่านั้น",
  ], 3, "แพลตฟอร์มให้รางวัลคนที่ active สม่ำเสมอ การบ้านบท 2.5 เลยให้ลงวันละ 1 ชิ้น 10 วันติด"),
]);

const M3 = quiz(3, "วาง Offer + สร้าง Landing Page", [
  q("q1", "\"สินค้า\" กับ \"Offer\" ต่างกันยังไง", [
    "เหมือนกัน เรียกต่างกันเฉยๆ",
    "สินค้าคือสิ่งที่คุณขาย ส่วน Offer คือสิ่งที่ลูกค้าคิดในหัวตอนกำลังตัดสินใจว่าจะซื้อหรือไม่ซื้อ",
    "Offer คือราคาที่ลดแล้ว",
    "Offer คือของแถม",
  ], 2, "หน้าเว็บสวยแค่ไหน ถ้า Offer ไม่ชัดก็ขายไม่ได้ อ่านบท 3.0 ก่อนลงมือทำหน้าขายเสมอ"),
  q("q2", "ขายไม่ออก แล้วแก้ด้วยการใส่ของเพิ่ม แถมเพิ่ม หรือลดราคา ทำไมยอดมักเท่าเดิม", [
    "เพราะยังลดราคาไม่พอ",
    "เพราะหน้าที่ของหน้าขายคือบอกว่าของคุณช่วยแก้ปัญหาอะไรให้เขา ไม่ใช่บอกว่าคุ้มแค่ไหน อัดของเยอะยิ่งทำให้คนรู้สึกเป็นภาระ",
    "เพราะลูกค้าไม่ชอบของแถม",
    "เพราะต้องยิงแอดก่อน",
  ], 2, "ปี 2026 คนอยากได้ Transformation ที่จับต้องได้ง่ายๆ AI ให้ข้อมูลได้หมดแล้ว และคนสมาธิสั้นลง"),
  q("q3", "ตามสมการ Value ของ Alex Hormozi อะไรทำให้มูลค่า Offer สูงขึ้น", [
    "เพิ่มราคาให้สูงขึ้น",
    "เพิ่ม Dream Outcome กับความเชื่อมั่นว่าทำได้จริง และลดเวลาที่ต้องรอกับความเหนื่อยที่ต้องลงแรง",
    "เพิ่มจำนวนวิดีโอในคอร์ส",
    "เพิ่มเวลาเรียนให้นานขึ้น",
  ], 2, "ตัวบน: Dream Outcome + Perceived Likelihood ยิ่งมากยิ่งดี ตัวล่าง: Time Delay + Effort & Sacrifice ยิ่งน้อยยิ่งดี"),
  q("q4", "ข้อไหนคือ Dream Outcome ที่ถูกต้องของคอร์สสอน Automation", [
    "ได้คลิปสอน 20 ชั่วโมง",
    "ได้ไฟล์ template 50 ไฟล์",
    "ได้ระบบปิดการขายที่ทำให้ไม่ต้องทำงานแอดมินเอง",
    "ได้ใบประกาศนียบัตร",
  ], 3, "Dream Outcome คือสิ่งที่ลูกค้าอยากได้จริงๆ เหมือนคนไม่ได้อยากได้คอร์สลดน้ำหนัก แต่อยากหุ่นดีและมั่นใจ"),
  q("q5", "หน้าขาย 1 หน้ามี 8 Section ข้อไหนเรียงลำดับถูก", [
    "Offer, Outcome, Problem, FAQ, Fit, Flow, Credit, Final CTA",
    "Outcome, Problem, Fit, Flow, Credit, FAQ, Offer, Final CTA",
    "Credit, FAQ, Outcome, Problem, Offer, Fit, Flow, Final CTA",
    "Problem, Offer, Final CTA, Outcome, Fit, Flow, Credit, FAQ",
  ], 2, "เรียงตามลำดับที่คนตัดสินใจจริง และทุก Section จบด้วยปุ่มซื้อ เพราะคนพร้อมซื้อไม่เท่ากัน"),
  q("q6", "พาดหัวที่ดีต้องตอบ 3 ข้ออะไร", [
    "ชื่อแบรนด์, ราคา, เบอร์ติดต่อ",
    "ได้อะไร, ง่ายไหม, ทำได้จริงไหม (ไม่เสี่ยง)",
    "ใครสอน, สอนกี่ชั่วโมง, มีกี่บท",
    "ลดกี่เปอร์เซ็นต์, ถึงวันไหน, เหลือกี่ที่",
  ], 2, "ตัวอย่างเติมทีละชั้น: สร้างระบบทำเงินอัตโนมัติ (ได้อะไร) ทำตามทีละขั้นรวมประมาณ 3 ชั่วโมง (ง่าย) หรือรับเงินคืน (ไม่เสี่ยง)"),
  q("q7", "ทำไมการบอกว่าสินค้า \"ไม่เหมาะกับใคร\" ถึงช่วยขาย", [
    "ไม่ช่วย ควรบอกว่าเหมาะกับทุกคน",
    "ทำให้คนที่ใช่เชื่อเรามากขึ้น ส่วนคนที่ไม่ใช่ ซื้อไปก็ไม่ได้ผลอยู่ดี",
    "ทำให้ขายได้แพงขึ้นทันที",
    "เป็นข้อบังคับทางกฎหมาย",
  ], 2, "ดิวเขียนตรงๆ ว่าไม่เหมาะกับคนที่คิดว่าจะรวยข้ามวัน \"ผมช่วยประหยัดเวลาลองผิดลองถูกให้ได้ แต่ลงมือแทนคุณไม่ได้\""),
  q("q8", "Section Flow (ซื้อแล้วเกิดอะไรขึ้น) ควรเน้นอะไร", [
    "เล่าทุกขั้นจนถึงขั้นสุดท้ายแบบละเอียดที่สุด",
    "บอกให้ชัดว่าขั้นแรกง่ายแค่ไหน และได้อะไรทันที",
    "ไม่ต้องมี เพราะทำให้หน้ายาว",
    "บอกเฉพาะราคา",
  ], 2, "คนกลัวเพราะมองไปไกลถึงขั้นสุดท้ายว่าจะทำได้ไหม เหมือนขึ้นบันได ทุกคนเริ่มจากขั้นแรก"),
  q("q9", "ถ้ายังไม่มีหลักฐานมากพอ (ยอดขาย รีวิว) ควรทำยังไงกับหน้าขาย", [
    "แต่งตัวเลขขึ้นมาก่อน",
    "ลดขนาดคำสัญญาลงให้พอดีกับหลักฐานที่มีจริง",
    "ไม่ต้องมี Section หลักฐานเลย แต่สัญญาใหญ่เหมือนเดิม",
    "ใช้รีวิวของคนอื่นมาใส่",
  ], 2, "ยิ่งสัญญาใหญ่ เครดิตต้องยิ่งใหญ่ตาม ใส่เฉพาะของจริง และเฉพาะอันที่ช่วยพยุงสินค้าที่ขาย"),
  q("q10", "Value Stack ทำหน้าที่อะไร และกติกาที่ห้ามข้ามคืออะไร", [
    "ทำให้ราคาดูแพง ใส่ตัวเลขสูงๆ ได้ตามใจ",
    "ให้ลูกค้ามีตัวเทียบว่าได้อะไรบ้าง มูลค่าเท่าไหร่ ก่อนบอกราคา และทุกตัวเลขต้องมีที่มาที่เขียนไว้บนหน้า",
    "บอกส่วนลดที่ได้",
    "บอกจำนวนชั่วโมงของคอร์ส",
  ], 2, "ถ้าไม่ให้ตัวเทียบ คนจะเทียบกับของที่ถูกที่สุดที่เคยเห็น ที่มาของตัวเลขเช่น ราคาจ้างฟรีแลนซ์ ราคาขายแยกจริง หรือ 10% ของเงินที่ความรู้นี้สร้างได้"),
  q("q11", "ทำไมควรมีราคา 3 แบบ (Decoy Choice)", [
    "เพื่อให้มีของขายเยอะ",
    "เพราะคำถามในหัวลูกค้าเปลี่ยนจาก \"ซื้อหรือไม่ซื้อ\" เป็น \"แบบไหนเหมาะกับฉัน\" และแบบแพงสุดทำให้แบบที่แนะนำดูคุ้มทันที",
    "เพราะลูกค้าชอบของแพง",
    "ยิ่งมีหลายแบบยิ่งดี ควรมี 5 แบบขึ้นไป",
  ], 2, "Starter 1,690 / Pro 1,990 (แนะนำ) / VIP 25,000 จ่ายเพิ่มจาก Starter แค่ 300 ได้มากกว่าเยอะ และไม่ควรเกิน 3 แบบ"),
  q("q12", "การรับประกันที่ดีควรเป็นแบบไหน", [
    "การันตีว่าจะขายได้เท่าคนสอนแน่นอน",
    "ไม่ต้องบอกเงื่อนไข",
    "บอกเงื่อนไขชัด และบอกด้วยว่าอะไรที่ไม่การันตี",
    "การันตีคืนเงินโดยไม่ต้องลองทำอะไรเลย",
  ], 3, "ดิวบอกว่าไม่การันตีว่าจะขายได้เท่าเขา แต่ถ้าทำตามแล้วสร้างเว็บกับระบบไม่ได้ คืนเงินใน 30 วัน ความตรงไปตรงมาทำให้คนเชื่อมากขึ้น"),
  q("q13", "ข้อไหนเป็น Urgency ที่ ไม่ควรทำ", [
    "ราคาขึ้นตามวันที่ประกาศไว้ชัด แล้วขึ้นจริง",
    "บอกว่าทุกเดือนที่รอ เขาเสียอะไรไป",
    "ตัวนับถอยหลังที่เริ่มนับใหม่ทุกครั้งที่เปิดหน้า หรือบอก \"เหลือ 2 ที่\" ทั้งที่ไม่จริง",
    "โบนัสเฉพาะรอบนี้ หมดแล้วหมดเลย",
  ], 3, "\"รอก่อนได้\" เกือบทุกครั้งแปลว่าไม่ซื้อ แต่ต้องเร่งด้วยของจริงเท่านั้น เร่งปลอมอาจได้ยอดครั้งเดียว แล้วลูกค้าจะไม่เชื่อคุณอีกเลย"),
  q("q14", "การขอรีวิวจากลูกค้าให้ได้คำตอบจริง ต้องมีอะไร", [
    "ทักไปขอรีวิวเฉยๆ ให้เขาเขียนเอง",
    "เตรียมคำถามที่ตอบง่ายไว้ให้เลย และมีของแถมเล็กๆ ให้คนที่ตอบ",
    "รอให้ลูกค้ารีวิวเองโดยไม่ต้องถาม",
    "บังคับให้รีวิวก่อนเข้าเรียน",
  ], 2, "ถ้าขอเฉยๆ ลูกค้าต้องคิดเยอะว่าจะตอบอะไร เลยไม่ตอบ ส่วนฟีดแบ็กเชิงบ่นก็มีค่ามาก เหมือนลูกค้าเอาทองมากองไว้ตรงหน้า"),
  q("q15", "หลังให้ AI สร้างหน้าขายด้วย Sales Page Kit เสร็จ สิ่งที่ต้องทำก่อนส่งให้คนอื่นดูคืออะไร", [
    "ส่งลิงก์จากไฟล์ในเครื่องให้ลูกค้าได้เลย",
    "ไล่อ่านตัวเลขและข้อความทุกจุดว่าตรงกับความจริง แล้ว Deploy ขึ้น Netlify จนเปิด URL จากมือถือได้",
    "ซื้อโดเมนราคาแพงก่อน",
    "รอให้ระบบจ่ายเงินเสร็จก่อนถึงจะ Deploy ได้",
  ], 2, "AI ชอบแต่งตัวเลขเอง และเว็บที่ยังไม่ Deploy เปิดได้แค่ในเครื่องเรา ถ้ายังไม่มีระบบจ่ายเงิน ใส่ลิงก์ LINE OA หรือ Messenger ไปก่อนได้"),
]);

// Module 4 has no quiz: its students hand in their finished website link instead (Dew, 2026-10-27).

const M5 = quiz(5, "ระบบผลิตคอนเทนต์ด้วย AI", [
  q("q1", "ติดตั้ง Content Skills ใน Claude ยังไง", [
    "ดาวน์โหลดแอปใหม่จาก App Store",
    "Customize > Add > Add plugin from repository แล้ววางลิงก์ GitHub ที่ดิวแจก",
    "พิมพ์คำว่า install ในแชท",
    "ส่งอีเมลขอให้ทีม Claude เปิดให้",
  ], 2, "ติดตั้งเสร็จ พิมพ์ / จะเห็น content skills ขึ้นมาให้เรียกใช้"),
  q("q2", "ทำไมต้องสร้าง Project ชื่อ Content Area แทนการคุยลอยๆ ในแชท", [
    "เพราะแชทธรรมดาใช้ skill ไม่ได้",
    "เพราะ Project ทำให้ AI ตอบไวขึ้น",
    "เพราะ Project เก็บ brand brief ไว้ ไม่ต้องอธิบายตัวเองใหม่ทุกรอบ",
    "เพราะ Project ฟรี แต่แชทเสียเงิน",
  ], 3, "ทำงานคอนเทนต์ใน Project เดียวทุกครั้ง AI จะจำได้ว่าแบรนด์เราคือใคร ขายอะไร ลูกค้าเป็นใคร"),
  q("q3", "Skill ตัวแรกที่ต้องรันคืออะไร และทำไม", [
    "Brand Brief เพื่อให้ AI รู้จักแบรนด์ สินค้า และลูกค้าของเราก่อน",
    "Content Coach เพื่อได้โพสต์ทันที",
    "Post Grader เพื่อให้คะแนนโพสต์เก่า",
    "Hook เพื่อเขียน hook ก่อนอย่างอื่น",
  ], 1, "Brand Brief คือข้อมูลตั้งต้นที่ skill อื่นทุกตัวใช้ต่อ ถ้าไม่มี AI จะเขียนแบบกลางๆ ไม่ใช่เสียงของเรา"),
  q("q4", "ตอน Brand Brief ถามว่า \"ขายอะไร\" ทางลัดที่ดิวแนะนำคืออะไร", [
    "พิมพ์อธิบายสั้นๆ 1 บรรทัด",
    "ก๊อปเนื้อหาหน้า Offer ที่เขียนไว้ใน Module 3 มาแปะเลย",
    "ส่งลิงก์ Facebook เพจ",
    "ข้ามคำถามนี้ไปก่อน",
  ], 2, "แปะเนื้อหา Offer เข้าไป AI เข้าใจทันทีว่าขายอะไร ให้ใคร ได้อะไร ดีกว่าพิมพ์อธิบายเองมาก"),
  q("q5", "คำถาม \"สิ่งเดียวที่อยากให้คนทำหลังเห็นโพสต์\" ควรตอบยังไง", [
    "ตอบว่าอยากได้ทั้ง 3 funnel",
    "ตอบว่ายังไม่รู้",
    "ตอบ action หลักอันเดียวตรงๆ ว่าอยากให้เขาทำอะไรท้ายที่สุด เช่น กดซื้อคอร์ส",
    "ตอบว่าอยากให้กดไลก์",
  ], 3, "ดิวตอบว่าอยากได้ทั้ง 3 funnel แล้ว AI สวนกลับว่าขอ action หลักอันเดียว เพราะเป้าหมายที่ชัดทำให้คอนเทนต์ชัด"),
  q("q6", "ในการตอบ Brand Brief คำถามข้อไหนที่ AI บอกว่าเป็น \"เชื้อไฟที่ทำให้โพสต์ไวรัลที่สุด\"", [
    "ราคาสินค้า",
    "ชื่อเพจ",
    "ความเห็นที่ขัดแย้งกับคนในวงการ",
    "สีประจำแบรนด์",
  ], 3, "เขียนให้เป็นประโยคเดียวที่คม เช่นของดิวเรื่อง Claude Cowork อันนี้จะถูกใช้ต่อยาวในคอนเทนต์"),
  q("q7", "ข้อจำกัดของ Claude แบบแชทธรรมดาที่ต้องรู้คืออะไร", [
    "ใช้ภาษาไทยไม่ได้",
    "เก็บไฟล์ไม่ได้ เปิดแชทใหม่แล้วมันลืม ต้องคุยอยู่ในแชทเดียว แชทจะตันและคอนเทนต์คนละวันเริ่มปนกัน",
    "เขียนโพสต์ยาวไม่ได้",
    "ใช้ skill ได้แค่วันละครั้ง",
  ], 2, "ถ้าจะทำจริงจัง ดิวแนะนำให้ใช้ตัวที่เก็บไฟล์ได้ (แบบเสียเงิน)"),
  q("q8", "เรียก Content Coach แล้วหัวข้อที่เสนอมาไม่ตรงกับสิ่งที่จะขาย ควรทำยังไง", [
    "ใช้หัวข้อที่เสนอมาไปเลย",
    "เลิกใช้ skill นี้",
    "สั่งใหม่ได้เลย บอกให้ชัดว่าอยากได้คอนเทนต์เพื่ออะไร เช่น ขายคอร์สที่กำลังเปิดพรีออเดอร์",
    "ลบ Project แล้วเริ่มใหม่",
  ], 3, "Content Coach เสนอ 5 หัวข้อเป็นจุดตั้งต้น แต่เราเป็นคนกำหนดเป้าหมาย สั่งเจาะจงได้เสมอ"),
  q("q9", "ระหว่างทำงาน Content Coach ตรวจคุณภาพงานตัวเองยังไง", [
    "ไม่ได้ตรวจ ต้องตรวจเองทั้งหมด",
    "เรียก skill อื่นต่อเอง เช่น Post Grader ให้คะแนน และ skill hook เขียน hook ตามเฟรมเวิร์ก",
    "ส่งไปให้ดิวตรวจ",
    "โพสต์ทดลองก่อนแล้วดูยอด",
  ], 2, "ถ้าคะแนนยังไม่พอใจ สั่งให้แก้จนได้เกิน 9 ได้เลย"),
  q("q10", "ทำไมดิวให้ใส่รูปหน้าตัวเองลงในรูปประกอบคอนเทนต์", [
    "เพราะ algorithm ให้คะแนนรูปคนมากกว่า",
    "เพราะไม่มีรูปอื่นใช้",
    "เพราะคนเชื่อมโยงกับคน อยากเห็นหน้าคนทำ ไม่ใช่เพจเปล่าๆ",
    "เพราะกฎหมายบังคับ",
  ], 3, "สั่ง AI ทำ infographic เป็น HTML แล้วเอาออกมาเป็น PNG ได้ และโยนรูปโปรไฟล์เข้าไปให้มันใส่ในภาพ"),
  q("q11", "ใช้ Canva template ของดิว ขั้นแรกต้องทำอะไร", [
    "แก้ตัวต้นฉบับได้เลย",
    "กด Make a copy ก่อนแก้ แล้วเปลี่ยนรูปหน้า ชื่อ และ user ID เป็นของเรา",
    "ลบทุกสไลด์ทิ้งก่อน",
    "เปลี่ยนสีทั้งหมดเป็นสีแบรนด์ก่อน",
  ], 2, "ไม่จำเป็นต้องใช้ template ของดิวก็ได้ ถ้าเจออันที่ชอบกว่า แต่ไม่ว่าใช้อันไหนต้องเปลี่ยนเป็นตัวตนของเรา"),
  q("q12", "ก่อนโพสต์คอนเทนต์ที่ AI เขียน ต้องทำอะไรเสมอ", [
    "โพสต์ทันที เพราะ AI ได้คะแนนเกิน 9 แล้ว",
    "ตรวจด้วยตัวเองอีกรอบ ว่าข้อมูลเกี่ยวกับธุรกิจเราถูกต้อง ไม่เกินจริง และไม่แซะคนอื่นจนเสียแบรนด์",
    "ส่งให้เพื่อนกดไลก์ก่อน",
    "แปลเป็นภาษาอังกฤษ",
  ], 2, "ในคลิป AI เขียนบางประโยคเวอร์และแซะคนอื่นเกินไป ดิวต้องแก้เอง สกิลที่ต้องมีคือการตัดสินและปรับแก้ ไม่ใช่ให้ AI ทำทุกอย่าง 100%"),
]);

const M6 = quiz(6, "AI x Viral Framework", [
  q("q1", "Viral Spy คืออะไร", [
    "แอปสำหรับซื้อยอดวิว",
    "logic การหา outlier content จาก Module 2 ที่ดิวแพ็กเป็น Agent Skill ให้ใช้กับคอนเทนต์ของเราได้เลย",
    "บอทคอมเมนต์อัตโนมัติ",
    "ปลั๊กอินแต่งรูป",
  ], 2, "แทนที่จะไล่ส่องช่องคู่แข่งเองทีละช่อง Viral Spy ดึงข้อมูลแล้วจัดอันดับหัวข้อที่โดดให้"),
  q("q2", "หยิบหัวข้อจากคอนเทนต์คนอื่นมาแล้ว ข้อไหน ไม่ควรทำ", [
    "เล่าในมุมของตัวเอง",
    "ใส่ประสบการณ์ของตัวเองเข้าไป",
    "ทำแค่คอนเทนต์สรุปของเขาเฉยๆ ไม่ใส่อะไรของเราเลย",
    "แตกเป็นมุมใหม่หลายๆ มุม",
  ], 3, "ดิวเตือนตั้งแต่ก่อนเริ่มติดตั้ง ถ้าจะสรุปต้องใส่มุมมองและประสบการณ์ของตัวเองเข้าไปเสมอ"),
  q("q3", "ทำไมต้องใช้ Claude Desktop ในการใช้ Viral Spy", [
    "เพราะเวอร์ชันเบราว์เซอร์ไม่มีภาษาไทย",
    "เพราะ Apify connector (ตัวดึงข้อมูลคู่แข่ง) ใช้บนเบราว์เซอร์ไม่ได้",
    "เพราะ Desktop ฟรี แต่เบราว์เซอร์เสียเงิน",
    "เพราะ Desktop เร็วกว่า 10 เท่า",
  ], 2, "ต่อ Apify ผ่าน Customize > Connectors แล้วใส่ Personal API Key จาก apify.com (Settings > API Integration)"),
  q("q4", "Apify ทำหน้าที่อะไรในระบบนี้", [
    "เขียนโพสต์ให้",
    "ดึงข้อมูลคอนเทนต์ของคู่แข่งมาให้ Viral Spy วิเคราะห์",
    "โพสต์คอนเทนต์ลงเพจอัตโนมัติ",
    "ทำรูปประกอบ",
  ], 2, "Viral Spy คือสมองที่วิเคราะห์ ส่วน Apify คือมือที่ไปดึงข้อมูลโพสต์และยอดวิวจากช่องต่างๆ มาให้"),
  q("q5", "วิธีนี้เหมาะกับใครเป็นพิเศษ", [
    "คนที่ยังไม่มีสินค้า",
    "คนที่ทำคอนเทนต์เท่าไหร่ก็ไม่ได้ attention เพราะได้หัวข้อที่พิสูจน์แล้วว่าคนสนใจ",
    "คนที่ไม่อยากทำคอนเทนต์เลย",
    "คนที่มีผู้ติดตามเกินล้านแล้ว",
  ], 2, "ใช้กฎของไวรัลจาก Module 2: หัวข้อที่เคยไวรัลแล้ว เล่าใหม่ก็มีโอกาสไวรัลอีก"),
  q("q6", "AI แนะนำเพจคู่แข่งเพิ่มมาให้ ดิวแนะนำให้ทำอะไรก่อนสั่งให้ดึงข้อมูล", [
    "รับมาทั้งหมดเลย ยิ่งเยอะยิ่งดี",
    "ลบทิ้งทั้งหมด ใช้แค่ลิสต์ของเราเอง",
    "ไปส่องเพจที่มันแนะนำมาทีละอัน ว่าชอบคอนเทนต์เขาไหม แล้วคัดอันที่คุณภาพไม่ถึงออก",
    "ให้ AI ตัดสินใจเองทั้งหมด",
  ], 3, "ดิวไม่เอาบางเพจที่ AI แนะนำ เพราะอยากได้เพจที่ทำโพสต์จริงจังกว่า ถ้าต้นทางไม่ดี หัวข้อที่ได้ก็ไม่ดี"),
  q("q7", "ข้อสังเกตของดิวเรื่องแพลตฟอร์มในวงการ AI (โดยเฉพาะต่างประเทศ) คืออะไร", [
    "คนส่วนใหญ่อยู่ Facebook",
    "คนส่วนใหญ่อยู่ YouTube, Instagram และ X ไม่ค่อยอยู่ Facebook",
    "คนส่วนใหญ่อยู่ LINE",
    "ทุกแพลตฟอร์มมีคนเท่ากัน",
  ], 2, "เลือกแพลตฟอร์มที่จะไปดึงข้อมูลให้ตรงกับที่คนในสายของคุณอยู่จริง"),
  q("q8", "ผลลัพธ์จาก Viral Spy ที่ได้ออกมาเป็นอะไร", [
    "โพสต์ที่พร้อมลงทันที",
    "ไฟล์ตาราง Top 10 หัวข้อแรงสุด มี parent, score, outlier score และ URL",
    "รายชื่อคนที่กดไลก์คู่แข่ง",
    "ยอดขายของคู่แข่ง",
  ], 2, "parent คือการจับกลุ่มหัวข้อที่คล้ายกันไว้ด้วยกัน ส่วน outlier score บอกว่าโดดกว่าฐานของช่องนั้นแค่ไหน"),
  q("q9", "ทำไมต้องกดเข้าไปเช็ค URL จริงในตารางทุกครั้ง", [
    "เพื่อกดไลก์ให้คู่แข่ง",
    "เพราะบางรายการตัวเลขไม่ตรง หรือ AI \"หลอน\" มา อย่าเชื่อตารางอย่างเดียว",
    "เพราะระบบบังคับ",
    "ไม่จำเป็นต้องเช็ค ตารางถูกเสมอ",
  ], 2, "การบ้าน Module 6 ให้เช็คอย่างน้อย 5 URL และต้องคัดหัวข้อทิ้งอย่างน้อย 1 อัน ถ้ารับมาหมดแปลว่ายังไม่ได้ตรวจ"),
  q("q10", "ใช้แพลนฟรีแล้วเครดิตหมดกลางทาง Viral Spy หยุดทำงาน ต้องทำยังไง", [
    "เริ่มใหม่ทั้งหมดตั้งแต่ต้น",
    "พิมพ์ว่า \"ต่อ\" มันก็ทำงานต่อ",
    "ลบ Project ทิ้ง",
    "รอ 1 เดือน",
  ], 2, "ไม่ต้องตกใจ งานที่ทำไปแล้วยังอยู่ แค่สั่งให้ต่อ"),
  q("q11", "ได้ไฟล์หัวข้อไวรัลแล้ว ขั้นต่อไปคืออะไร", [
    "โพสต์หัวข้อในตารางตรงๆ ทันที",
    "โยนไฟล์ผลลัพธ์เข้า Project Content Area ที่มี Content Coach แล้วสั่งให้แนะนำและเขียนคอนเทนต์ 5 ชิ้น แล้วรีวิวเองก่อนลง",
    "ส่งไฟล์ให้คู่แข่ง",
    "ยิงแอดหัวข้อทั้ง 10 อัน",
  ], 2, "ทำงานเป็น 2 Project: Viral Topic Research หาหัวข้อ, Content Area เขียนคอนเทนต์ด้วยเสียงแบรนด์ของเรา"),
  q("q12", "ถ้าอยากเร่งให้ขายได้ไวขึ้น ดิวฝากอะไรไว้นอกจากใช้ AI เขียนคอนเทนต์", [
    "ซื้อผู้ติดตาม",
    "ทำคอนเทนต์เล่าสตอรี่ตัวเอง และออกกล้องด้วย เพราะทำให้คนรู้จักและเชื่อใจเรามากขึ้น",
    "โพสต์แต่ Bottom Funnel",
    "ไม่ต้องรีวิว ให้ AI โพสต์เองทั้งหมด",
  ], 2, "ไม่ออกกล้องก็ยังขายได้ (ดิวเองก็เคยไม่ออก) แต่การออกกล้องช่วยเร่งความเชื่อใจได้มาก"),
]);

const M7 = quiz(7, "Scale ด้วย Ads", [
  q("q1", "Cold Lead, Warm Lead และ Customer คือใคร ตามลำดับ", [
    "คนที่จ่ายเงินแล้ว / คนที่ไม่รู้จักเรา / คนที่เริ่มเชื่อใจเรา",
    "คนที่ไม่เคยรู้จักเพจเรา / คนที่เห็นเพจเรามาสักพักและเริ่มเชื่อใจ / คนที่จ่ายเงินให้เรา",
    "คนที่กดไลก์ / คนที่แชร์ / คนที่คอมเมนต์",
    "ลูกค้าเก่า / ลูกค้าใหม่ / ลูกค้าที่ refund",
  ], 2, "การยิงแอดทั้งหมดใน Module นี้มีงานแค่ 2 อย่าง: พา Cold เป็น Warm และพา Warm เป็น Customer"),
  q("q2", "ใน Ads Manager โครงสร้าง 3 ชั้นทำหน้าที่อะไร", [
    "Campaign เลือกคนเห็น / Ad Set เลือกเป้าหมาย / Ad Creative เลือกงบ",
    "Campaign คือเป้าหมายของโฆษณา / Ad Set คือเลือกว่าใครเห็น / Ad Creative คือตัวโฆษณาที่คนเห็นจริง",
    "ทั้ง 3 ชั้นคือที่ตั้งงบประมาณ",
    "Campaign คือรูป / Ad Set คือข้อความ / Ad Creative คือราคา",
  ], 2, "Campaign บอก Facebook ว่าอยากได้อะไร เช่น Engagement หรือ Sales"),
  q("q3", "Ads ชุดที่ 1 (Cold เป็น Warm) ตั้งค่าแบบไหน", [
    "Campaign: Sales / Ad Set: Engage Page 15 วัน / Creative: โพสต์ขายพร้อมราคา",
    "Campaign: Engagement / Ad Set: Lookalike Engage Page 90 วัน / Creative: โพสต์ Organic เก่าที่เคยมีคน Engage เยอะ",
    "Campaign: Traffic / Ad Set: ทุกคนในประเทศ / Creative: โปรโมชัน",
    "Campaign: Sales / Ad Set: Lookalike / Creative: รีวิวลูกค้า",
  ], 2, "ด่านแรกยังไม่ขาย แค่ให้คนที่ไม่รู้จักเราได้เห็นคอนเทนต์ที่ดีที่สุดของเราจนเริ่มมี Action กับเพจ"),
  q("q4", "Lookalike Engage Page คือกลุ่มเป้าหมายแบบไหน", [
    "คนที่เคย Engage กับเพจเราแล้ว",
    "คนใหม่ที่ Facebook หามาให้ ซึ่งมีนิสัยคล้ายกับคนที่เคยมี Action กับเพจเรา",
    "เพื่อนของเราใน Facebook",
    "คนที่เคยซื้อของจากเราแล้ว",
  ], 2, "Lookalike แปลว่าคนที่หน้าตาคล้าย ไม่เคยรู้จักเรา แต่มีแนวโน้มสนใจเรื่องเดียวกัน"),
  q("q5", "ทำไมด่านแรกควรเอาโพสต์ Organic เก่าที่เคยปังมายิง แทนการทำโฆษณาใหม่", [
    "เพราะประหยัดเวลาทำรูป",
    "เพราะโพสต์นั้นพิสูจน์มาแล้วว่าคนชอบ (คนดูเยอะ Engagement สูง)",
    "เพราะ Facebook บังคับ",
    "เพราะโพสต์เก่าไม่ต้องจ่ายค่าแอด",
  ], 2, "ใช้หลักเดียวกับกฎของไวรัลใน Module 2 ของที่พิสูจน์แล้ว ความเสี่ยงต่ำกว่าของใหม่"),
  q("q6", "Cold Lead จะกลายเป็น Warm Lead เมื่อไหร่", [
    "เมื่อจ่ายเงิน",
    "เมื่อมี Action อะไรสักอย่างกับเพจ เช่น อ่านจนจบ กดไลก์ คอมเมนต์ แชร์ (เข้ากลุ่ม Engage Page อัตโนมัติ)",
    "เมื่อกดติดตามเพจเท่านั้น",
    "เมื่อเราส่งข้อความหา",
  ], 2, "เราไม่ต้องทำอะไรเพิ่ม Facebook นับเขาเข้ากลุ่ม Engage Page เอง แล้วเขาจะไปเจอ Ads ชุดที่ 2"),
  q("q7", "Ads ชุดที่ 2 (Warm เป็น Customer) ตั้งค่าแบบไหน", [
    "Campaign: Engagement / Ad Set: Lookalike 90 วัน",
    "Campaign: Sales / Ad Set: คนที่เคย Engage กับเพจ เช่น Engage Page 15 วัน หรือ 60 วัน",
    "Campaign: Awareness / Ad Set: ทุกคน",
    "Campaign: Traffic / Ad Set: คนที่ไม่เคยรู้จักเพจ",
  ], 2, "คนกลุ่มนี้เริ่มไว้ใจเราแล้ว ถึงเวลาทำให้เขาเห็นว่าเขามีปัญหา และเราช่วยแก้ได้"),
  q("q8", "Creative ของ Ads ชุดที่ 2 มี 2 แบบ คืออะไร", [
    "มีม และ คลิปตลก",
    "จี้ให้เห็นปัญหาชัดขึ้น และ ทำให้ตัดสินใจง่าย (โพสต์ขายพร้อมราคา รีวิวลูกค้า โปรโมชัน)",
    "คลิปยาว และ ไลฟ์สด",
    "รูปสินค้า และ โลโก้",
  ], 2, "ตัวอย่างจี้ปัญหาของดิว: \"เป็นเจ้าของธุรกิจออนไลน์ แต่ต้องมานั่งส่งของเหมือนแอดมิน คุณกำลังเป็นเจ้าของ หรือกำลังเป็นกรรมกรในธุรกิจตัวเอง\""),
  q("q9", "ทำไมถึงไม่ควรยิงแอดขายของตรงๆ ไปหาคนที่ไม่เคยรู้จักเราเลย", [
    "เพราะผิดนโยบาย Facebook",
    "เพราะคนยังไม่เชื่อใจเรา ต้องพาเขาจาก Cold เป็น Warm ก่อน แล้วค่อยขาย",
    "เพราะแอดขายของแพงกว่า",
    "เพราะคนไม่เห็นโฆษณาขายของ",
  ], 2, "เหมือนเคส \"นิด\" ที่เห็นโพสต์ความรู้ก่อน กดไลก์ แล้วสัปดาห์ต่อมาค่อยเห็นโพสต์จี้ปัญหา รีวิว และโพสต์ขาย จนกดซื้อ"),
  q("q10", "ทำไม Module นี้ถึงอยู่ท้ายสุด และไม่ควรข้ามมาเรียนก่อน", [
    "เพราะเนื้อหาน้อยที่สุด",
    "เพราะแอดคือการ Scale ระบบที่ทำงานอยู่แล้ว ถ้ายังไม่เคยขายได้หรือยังไม่มีคนดู ยิงแอดก็ขยายของที่ยังไม่เวิร์ก",
    "เพราะต้องรอ Facebook อนุมัติ",
    "เพราะใครก็ยิงแอดได้โดยไม่ต้องเรียน",
  ], 2, "ยังไม่เคยมียอดขาย ไม่เคยมีคนดู ห้ามข้ามไปยิงแอด (จาก Module 1: 1 ปัญหา 1 Offer 1 กลุ่มลูกค้า และเดินทีละ step)"),
  q("q11", "ตัดสินใจเพิ่มหรือลดงบแอด ควรดูตัวเลขจากไหน", [
    "ROAS ที่ Meta รายงาน",
    "ยอดขายจริงใน Stripe",
    "จำนวนไลก์ของโพสต์",
    "ยอด Reach",
  ], 2, "ตัวเลขยอดซื้อที่ Meta รายงานมักไม่ตรงกับเงินที่เข้าจริง การบ้าน Module 7 ให้ทำตารางเทียบ 7 วัน Meta กับ Stripe"),
  q("q12", "สัญญาณว่าแอดตัวไหนเริ่ม \"อิ่มตัว\" คืออะไร", [
    "ความถี่ (Frequency) เกิน 3 คือคนกลุ่มเดิมเห็นซ้ำหลายรอบแล้ว",
    "ยอดไลก์เพิ่มขึ้น",
    "งบหมดเร็ว",
    "มีคนแชร์",
  ], 1, "พอคนเดิมเห็นซ้ำหลายรอบ ผลจะตกลง ถึงเวลาเปลี่ยน Creative หรือขยายกลุ่มเป้าหมาย"),
  q("q13", "Campaign มีให้เลือกหลายแบบ ดิวบอกให้โฟกัสแบบไหนเป็นหลัก (ราว 90%)", [
    "Awareness",
    "Traffic",
    "Sales",
    "App promotion",
  ], 3, "Sales ใช้ยิงคอนเทนต์ Middle และ Bottom Funnel ส่วน Engagement ใช้เมื่อมีงบเหลือและอยากดันโพสต์ Organic ให้คนเห็นเพิ่ม Traffic ไม่จำเป็น"),
  q("q14", "ถ้าเพิ่งเริ่มยิงแอด ดิวแนะนำให้เริ่มจากกลุ่มเป้าหมายไหน", [
    "ยิงในเพจก่อน (คนที่เคยมีส่วนร่วมกับเพจ) เพราะปิดการขายง่ายกว่า ส่วนคนใหม่ให้หาจากคอนเทนต์ Organic",
    "Lookalike ทั้งหมดตั้งแต่วันแรก",
    "ยิงหาทุกคนในประเทศ",
    "ยิงหาคนที่ไม่ชอบเพจเรา",
  ], 1, "Lookalike คือคนที่ยังไม่รู้จักเรา ปิดการขายยากกว่า เหมาะตอนที่ระบบเวิร์กแล้วและอยากให้หาคนใหม่แบบอัตโนมัติ"),
  q("q15", "ตอนสร้าง Ad Creative ดิวเลือกวิธีไหน", [
    "สร้างโฆษณาใหม่ในหน้า Ads Manager ทุกครั้ง",
    "ใช้โพสต์ที่มีอยู่แล้วในเพจ (Use existing post) ที่พิสูจน์แล้วว่าเวิร์ก และทำหลายตัว 3-4 แอดเพื่อทดสอบ",
    "ใช้รูปสินค้าอย่างเดียวไม่มีข้อความ",
    "ยิงแอดเดียวไปตลอด",
  ], 2, "โพสต์ที่เคยมีคนสนใจหรือเคยขายได้จริงลดความเสี่ยง และการกด Duplicate ทำหลายแอดช่วยให้เห็นว่าตัวไหนเวิร์กที่สุด"),
]);

export const QUIZ_SETS: Record<string, CheckpointSet> = {
  [M1.id]: M1,
  [M2.id]: M2,
  [M3.id]: M3,
  [M5.id]: M5,
  [M6.id]: M6,
  [M7.id]: M7,
};
